import { Navigate, createBrowserRouter } from "react-router-dom";
import Dashboard from "./pages/dashboard";
import LoginPage from "./pages/login";
import SelectOrganization from "./pages/select-organization";
import ScopeOne from "./pages/dashboard/scope1";
import ScopeTwo from "./pages/dashboard/scope2";
import ScopeThree from "./pages/dashboard/scope3";
import DashboardLayout from "./components/dashboard/DashboardLayout";
import Others from "./pages/dashboard/others";
import BusinessStructure from "./pages/business-structure";
import ProtectedRoutes from "./utils/protectedRoutes";
import Assets from "./pages/business-structure/Assets";
import BusinessProfile from "./pages/business-structure/BusinessProfile";
import MonthlyActivities from "./pages/setting/activity-templates/ActivityTemplates";
import Account from "./pages/account";
import Profile from "./pages/account/Profile";
import { AccessControl } from "./pages/setting/access-control/AccessControl";
import ActivatePage from "./pages/activate";
import ResetPassword from "./pages/account/ResetPassword";
import { ImportData } from "./pages/import-data/ImportData";
import { TgoReport } from "./pages/report/tgo-cfo/TgoReport";
import { ReportSummary } from "./pages/report/summary/ReportSummary";
import { AccessilityAndPermission } from "./pages/setting/accessility-permission/AccessilityAndPermission";
import { EconomicIndicator } from "./pages/setting/economic-indicator/EconomicIndicator";
import AddEmission from "./pages/emission";
import { EcoEfficiency } from "./pages/eco-efficiency/add-data";
import { FilesManagement } from "./pages/setting/files-management";
import EcoEfficiencyDashboard from "./pages/eco-efficiency/dashboard";
import DashboardGoalSetting from "./pages/goal-setting/dashboard";
import AddGoalSetting from "./pages/goal-setting/add-goal";
import { ApiKeysPage } from "./pages/setting/api-keys";
import NotFoundPage from "./pages/error/NotFoundPage";
import FinancedEmissionsLayout, {
  FeIndexRedirect,
} from "./pages/financed-emissions/FinancedEmissionsLayout";
import FinancedEmissionsDashboard from "./pages/financed-emissions/dashboard";
import Borrowers from "./pages/financed-emissions/borrowers";
import FinancedEmissionsReport from "./pages/financed-emissions/report";
import MyData from "./pages/financed-emissions/my-data";
import NeedsAttention from "./pages/financed-emissions/needs-attention";
import ForgotPasswordPage from "./pages/forgot-password";
import ResetPasswordPage from "./pages/reset-password";
import UnlockPage from "./pages/unlock";
import SsoLandingPage from "./pages/auth-sso";
import SsoFirstBindPage from "./pages/auth-sso-first-bind";
import SsoLinkPage from "./pages/auth-link";
import OnboardingPage from "./pages/onboarding";
import { IS_SSO_ENABLED } from "./constants";

// use ProtectedRoutes for page is required auth

export const router = createBrowserRouter([
  {
    path: "/",
    element: <Navigate to="/overview/total-emissions" replace />,
    errorElement: <NotFoundPage />, // ✅ ใส่ตรงนี้! มันจะดัก Error และ 404 ทั้งหมด
  },
  {
    path: "/overview",
    element: (
      <ProtectedRoutes>
        <DashboardLayout />
      </ProtectedRoutes>
    ),

    children: [
      {
        index: true,
        element: <Navigate to="/overview/total-emissions" replace />,
      },
      {
        path: "/overview/total-emissions",
        element: <Dashboard />,
      },
      {
        path: "/overview/scope-1",
        element: <ScopeOne />,
      },
      {
        path: "/overview/scope-2",
        element: <ScopeTwo />,
      },
      {
        path: "/overview/scope-3",
        element: <ScopeThree />,
      },
      {
        path: "/overview/other",
        element: <Others />,
      },
    ],
  },
  {
    path: "/emission",
    children: [
      {
        index: true,
        element: <Navigate to="/emission/add" replace />,
      },
      {
        path: "/emission/add",
        element: (
          <ProtectedRoutes>
            <AddEmission />
          </ProtectedRoutes>
        ),
      },
      {
        path: "/emission/import-data",
        element: (
          <ProtectedRoutes>
            <ImportData />
          </ProtectedRoutes>
        ),
      },
    ],
  },
  {
    path: "/eco-efficiency",
    children: [
      {
        index: true,
        element: <Navigate to="/eco-efficiency/dashboard" replace />,
      },
      {
        path: "/eco-efficiency/dashboard",
        element: (
          <ProtectedRoutes>
            <EcoEfficiencyDashboard />
          </ProtectedRoutes>
        ),
      },
      {
        path: "/eco-efficiency/add-data",
        element: (
          <ProtectedRoutes>
            <EcoEfficiency />
          </ProtectedRoutes>
        ),
      },
    ],
  },
  {
    path: "/goal-setting",
    children: [
      {
        index: true,
        element: <Navigate to="/goal-setting/dashboard" replace />,
      },
      {
        path: "/goal-setting/dashboard",
        element: (
          <ProtectedRoutes>
            <DashboardGoalSetting />
          </ProtectedRoutes>
        ),
      },
      {
        path: "/goal-setting/add-goal",
        element: (
          <ProtectedRoutes>
            <AddGoalSetting />
          </ProtectedRoutes>
        ),
      },
    ],
  },
  {
    path: "/report",
    children: [
      {
        index: true,
        element: <Navigate to="/report/tgo-cfo" replace />,
      },
      {
        path: "/report/tgo-cfo",
        element: (
          <ProtectedRoutes>
            <TgoReport />
          </ProtectedRoutes>
        ),
      },
      {
        path: "/report/summary",
        element: (
          <ProtectedRoutes>
            <ReportSummary />
          </ProtectedRoutes>
        ),
      },
    ],
  },
  {
    path: "/financed-emissions",
    element: (
      <ProtectedRoutes>
        <FinancedEmissionsLayout />
      </ProtectedRoutes>
    ),
    children: [
      {
        index: true,
        element: <FeIndexRedirect />,
      },
      {
        path: "/financed-emissions/dashboard",
        element: <FinancedEmissionsDashboard />,
      },
      {
        // A borrower detail is its own page — the eye icon in the list routes
        // there, and "Back to Borrowers" routes back. Nested so the detail
        // still matches /financed-emissions/borrowers for the breadcrumb.
        path: "/financed-emissions/borrowers",
        children: [
          { index: true, element: <Borrowers /> },
          { path: ":borrowerId", element: <Borrowers /> },
        ],
      },
      {
        path: "/financed-emissions/report",
        element: <FinancedEmissionsReport />,
      },
      {
        path: "/financed-emissions/needs-attention",
        element: <NeedsAttention />,
      },
      {
        path: "/financed-emissions/my-data",
        element: <MyData />,
      },
    ],
  },
  {
    path: "/setting",
    children: [
      {
        index: true,
        element: <Navigate to="/setting/activities-template" />,
      },
      {
        path: "/setting/activities-template",
        element: (
          <ProtectedRoutes>
            <MonthlyActivities />
          </ProtectedRoutes>
        ),
      },
      {
        path: "/setting/access-control",
        element: (
          <ProtectedRoutes>
            <AccessControl />
          </ProtectedRoutes>
        ),
      },
      {
        path: "/setting/accessility-and-permission",
        element: (
          <ProtectedRoutes>
            <AccessilityAndPermission />
          </ProtectedRoutes>
        ),
      },
      {
        path: "/setting/business-structure",
        element: (
          <ProtectedRoutes>
            <BusinessStructure />
          </ProtectedRoutes>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="/setting/business-structure/profile" />,
          },
          {
            path: "/setting/business-structure/profile",
            element: (
              <ProtectedRoutes>
                <BusinessProfile />
              </ProtectedRoutes>
            ),
          },
          {
            path: "/setting/business-structure/asset",
            element: (
              <ProtectedRoutes>
                <Assets />
              </ProtectedRoutes>
            ),
          },
        ],
      },
      {
        path: "/setting/economic-indicator",
        element: (
          <ProtectedRoutes>
            <EconomicIndicator />
          </ProtectedRoutes>
        ),
      },
      {
        path: "/setting/files-management",
        element: (
          <ProtectedRoutes>
            <FilesManagement />
          </ProtectedRoutes>
        ),
      },
      {
        path: "/setting/api-keys",
        element: (
          <ProtectedRoutes>
            <ApiKeysPage />
          </ProtectedRoutes>
        ),
      },
    ],
  },
  {
    path: "/account",
    element: (
      <ProtectedRoutes>
        <Account />
      </ProtectedRoutes>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/account/profile" />,
      },
      {
        path: "/account/profile",
        element: (
          <ProtectedRoutes>
            <Profile />
          </ProtectedRoutes>
        ),
      },
      {
        path: "/account/reset-password",
        element: (
          <ProtectedRoutes>
            <ResetPassword />
          </ProtectedRoutes>
        ),
      },
    ],
  },
  {
    path: "/login",
    element: <LoginPage />,
  },
  // Partner SSO landings — register เฉพาะตอน REACT_APP_ENABLE_SSO=true เท่านั้น
  // env ที่ไม่เปิดใช้ feature จะ fall through ไป NotFoundPage ที่ root route จับ
  // /auth/link ไม่ wrap ProtectedRoutes เพราะมันจะตัด linkToken ทิ้งตอน redirect — หน้านี้
  // คุม auth-bounce เอง เพื่อใส่ ?returnTo=... รักษา linkToken ข้าม login
  ...(IS_SSO_ENABLED
    ? [
        { path: "/auth/sso", element: <SsoLandingPage /> },
        { path: "/auth/sso/first-bind", element: <SsoFirstBindPage /> },
        { path: "/auth/link", element: <SsoLinkPage /> },
        {
          path: "/onboarding",
          element: (
            <ProtectedRoutes>
              <OnboardingPage />
            </ProtectedRoutes>
          ),
        },
      ]
    : []),
  {
    path: "/forgot-password",
    element: <ForgotPasswordPage />,
  },
  {
    path: "/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    path: "/select-organization",
    element: (
      <ProtectedRoutes>
        <SelectOrganization />
      </ProtectedRoutes>
    ),
  },
  {
    path: "/activate",
    element: <ActivatePage />,
  },
  {
    path: "/unlock",
    element: <UnlockPage />,
  },
]);
